import { CCol, CInput, CInputRadio, CRow } from "@coreui/react"
import classNames from "classnames"
import ToasterProvider from "components/common/Context/ToasterContext"
import { withErrorBoundary } from "components/common/error"
import { LoadingSpinnerOverlay } from "components/common/loading"
import Tooltip from "components/common/tooltip/Tooltip"
import { FormActions } from "core/modules/service/components/serviceRequest"
import { postSurvey } from "extensions/usoc/api/cp"
import React, { useContext, useState } from "react"
import { useSelector } from "react-redux"
import { useExtension } from "utils"
import getDropdownItems from "utils/array/getDropdownItems"
import { useUserAuth } from "utils/hooks"

import { Drawer } from "../drawer"
import { OptionDropdown } from "../dropdown"

export default withErrorBoundary(function ({
  isOpen,
  setSidebarIsOpen = () => { }
}) {
  const auth = useUserAuth()
  const projectID = useSelector((state) => {
    return state.settings.currentProject
  })
  const userEmail = useSelector((state) => {
    return state.user.email
  })
  const userRole = useSelector((state) => {
    return state.user.role
  })

  const extension = useExtension()
  const host = extension?.config?.env?.clientApiGateway?.URL

  const enqueueToast = useContext(ToasterProvider)

  const [isHappy, setIsHappy] = useState("Yes")
  const [improvements, setImprovements] = useState("")
  const [concerns, setConcerns] = useState("")
  const [isOptingResponses, setIsOptingResponses] = useState(true)
  const [isAnonymous, setIsAnonymous] = useState(true)
  const [isConcernsTouched, setIsConcernsTouched] = useState()

  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async () => {
    setIsConcernsTouched(true)
    if (isHappy === "No" && !concerns) return
    setIsLoading(true)
    const surveyBody = {
      happy_cms: isHappy,
      improvements,
      concerns,
      response: isOptingResponses ? "Yes" : "No",
      anonymous: isOptingResponses ? "No" : isAnonymous ? "Yes" : "No"
    }
    if (isOptingResponses || !isAnonymous) {
      surveyBody.email_id = userEmail
    }
    const response = await postSurvey(
      { auth, host, body: JSON.stringify(surveyBody) },
      projectID
    )
    if (!response.error && response.data) {
      setIsConcernsTouched(false)
      enqueueToast("success", null, "Survey submitted successfully")
      setSidebarIsOpen(false)
    } else enqueueToast("failure", null, "Could not submit the survey")
    setIsLoading(false)
  }

  const isInternalUser = userRole === "Portal-User"

  return (
    <Drawer
      isOpen={isOpen}
      onClose={() => setSidebarIsOpen(false)}
      heading="OpenCloud Portal Survey"
    >
      <div className="service-request-container feedback-form pr-5 feedback-background h-100">
        <LoadingSpinnerOverlay isLoading={isLoading} spinnerSize="8rem">
          <CRow className="m-3 pr-3">
            <CCol md="12">
              <p className="field-label font-weight-bold">
                Deloitte Cloud Managed Services (CMS) Client Satisfaction
                Survey.
              </p>
            </CCol>
          </CRow>
          <CRow className="m-3 pr-3">
            <CCol md="12">
              <p className="field-label">
                1. Are you happy with OpenCloud Services?
              </p>
            </CCol>
            <CCol md="12">
              <OptionDropdown
                items={getDropdownItems(["Yes", "No"])}
                value={isHappy}
                onChange={(val) => {
                  setIsHappy(val)
                  setIsConcernsTouched(false)
                }}
                className="w-100"
                buttonClassName="w-100 form-control"
                showDownIcon
              />
            </CCol>
          </CRow>
          <CRow className="m-3 pr-3">
            <CCol md="12">
              <p className="field-label">
                2. Is there anything we can do to improve the services, please
                describe?
              </p>
            </CCol>
            <CCol md="12">
              <CInput
                type="text"
                name="improvements"
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
              />
            </CCol>
          </CRow>
          <CRow className="m-3 pr-3">
            <CCol md="12">
              <p className="field-label">3. Please provide your concerns.</p>
            </CCol>
            <CCol md="12">
              <CInput
                type="text"
                name="concerns"
                value={concerns}
                onChange={(e) => {
                  setConcerns(e.target.value)
                  setIsConcernsTouched(true)
                }}
                className={classNames({
                  "is-invalid":
                    isHappy === "No" && isConcernsTouched && !concerns
                })}
              />
              <div className="invalid-feedback" data-testid={"concerns-error"}>
                This Field is Required
              </div>
            </CCol>
          </CRow>
          <CRow className="m-3 pr-3">
            <CCol md="4">
              <span className="field-label">
                Would you like a Response?
                <Tooltip
                  className="ml-2"
                  text="Please note: Survey cannot be submitted anonymously if response is requested back from CMS Team."
                />
              </span>
            </CCol>

            <CCol md="1">
              <CInputRadio
                className="mr-2 ml-1"
                checked={isOptingResponses}
                onChange={() => setIsOptingResponses(true)}
              />
              <p
                className={`field-label ml-4 pl-2 mt-1 ${isOptingResponses ? "font-weight-bold" : ""
                  }`}
              >
                Yes
              </p>
            </CCol>
            <CCol md="1">
              <CInputRadio
                className="mr-2 ml-1"
                checked={!isOptingResponses}
                onChange={() => setIsOptingResponses(false)}
              />
              <p
                className={`field-label ml-4 pl-2 mt-1 ${!isOptingResponses ? "font-weight-bold" : ""
                  }`}
              >
                No
              </p>
            </CCol>
          </CRow>
          <CRow className="m-3 pr-3">
            <CCol md="4">
              <p className="field-label">
                Would you like to Submit Anonymously?
              </p>
            </CCol>
            <CCol md="1">
              <CInputRadio
                disabled={isOptingResponses}
                className="mr-2 ml-1"
                checked={isAnonymous}
                onChange={() => setIsAnonymous(true)}
              />
              <p
                className={`field-label ml-4 pl-2 mt-1 ${isAnonymous ? "font-weight-bold" : ""
                  } ${isOptingResponses ? "greyed-font" : ""}`}
              >
                Yes
              </p>
            </CCol>
            <CCol md="1">
              <CInputRadio
                disabled={isOptingResponses}
                className="mr-2 ml-1"
                checked={!isAnonymous}
                onChange={() => setIsAnonymous(false)}
              />
              <p
                className={`field-label ml-4 pl-2 mt-1 ${!isAnonymous ? "font-weight-bold" : ""
                  } ${isOptingResponses ? "greyed-font" : ""}`}
              >
                No
              </p>
            </CCol>
          </CRow>
          {isInternalUser && (
            <div className="px-4 mx-1 text-danger">
              {"*This service is not available for internal users"}
            </div>
          )}
          {isOpen && (
            <FormActions
              onSubmit={handleSubmit}
              onCancel={() => setSidebarIsOpen(false)}
              className="pr-4"
              isSubmitDisabled={isInternalUser}
            />
          )}
        </LoadingSpinnerOverlay>
      </div>
    </Drawer>
  )
})
